import React from 'react';
import { Box, Typography, Grid, Modal, Tooltip, IconButton, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { ShieldCheck, LogOut } from 'lucide-react';
import BackrrLogo from 'assets/backrr_logo.png';
import SectionCard from 'ui-components/SectionCard';
import { Primary, Secondary } from 'ui-components/Buttons';
import { FetchClaimProfileInvestorDetails } from 'api/profile';
import Cookies from 'js-cookie';
import { FetchAllInvestorTypes } from 'api/utils';
import ImageAvatar from 'ui-components/ImageAvatar';
import { checkForAuthorization, checkForDomain, getBucketUrl, navigateToMain } from 'utils/index';
import Wrapper from '../Wrapper';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTheme } from '@emotion/react';
import { RESET_STATE } from 'store/actions';
import PersonalDetails from './PersonalDetailsPopup';
import { ReactComponent as CloseIcon } from "assets/close_icon.svg"

// Presentation tokens, matching the onboarding-v2 flow this page sits in.
const PAGE_BG = '#fffdfa';
const INDIGO = '#4F46E5';
const MUTED = '#667085';
const HEADING = '#101828';
const LABEL = '#344054';

// The API returns {} for fields it has no value for, so only a real string counts.
const hasText = (value) => typeof value === 'string' && value.trim().length > 0;

const ClaimProfilePage = () => {
    // console.log("deded")
    const investor_id = Cookies.get('X-INV-TEMP-ID') || Cookies.get('X-INV-ID');
    const { data: investorData, isLoading: isLoadingInvestorData } = FetchClaimProfileInvestorDetails({
        investor_id
    });
    const { data: investor_types, isLoading: isLoadingInvestorTypes } = FetchAllInvestorTypes({ investor_id: null });
    const navigate = useNavigate();
    const theme = useTheme();
    const dispatch = useDispatch()
    const currentIP = window.location.host;
    const handleLogOut = () => {
        dispatch({ type: RESET_STATE });
        Cookies.remove('X-INV-JWT', { path: '/' });
        Cookies.remove('X-INV-JWT', { domain: checkForDomain() });
        Cookies.remove('X-INV-ID', { path: '/' });
        Cookies.remove('X-INV-ID', { domain: checkForDomain() });
        Cookies.remove('name', { path: '/' });
        Cookies.remove('user_id', { path: '/' });
        Cookies.remove('startup_id', { path: '/' });
        Cookies.remove('email', { path: '/' });
        Cookies.remove('is_profile_complete', { path: '/' });
        Cookies.remove('startup_id', { path: '/' });
        Cookies.remove('step', { path: '/' });
        Cookies.remove('permissions', { path: '/' });
        Cookies.remove('ur', { path: '/' });
        Cookies.remove('search_startups', { path: '/' });
        Cookies.remove('search_backrr_startups', { path: '/' });
        Cookies.remove('backrr_startups_query', { path: '/' });
        Cookies.remove('backrr_startups_status', { path: '/' });
        const cookies = document.cookie.split(";");
        cookies.forEach(cookie => {
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
        });
        navigate('/login');
    };

    // console.log(investor_types,"investor_types")

    const isAngelInvestor = investorData?.data?.investor_type?.includes("angel");

    const profileData = {
        email: investorData?.data?.email,
        first_name: investorData?.data?.first_name,
        last_name: investorData?.data?.last_name,
        full_name: investorData?.data?.first_name + " " + investorData?.data?.last_name,
        name: isAngelInvestor ? investorData?.data?.first_name + " " + investorData?.data?.last_name : typeof investorData?.data?.organization_name === "string" ? investorData?.data?.organization_name : null,
        investor_type: investor_types?.find(type => type?.value === investorData?.data?.investor_type)?.label,
        image: getBucketUrl() + investorData?.data?.media_dir + "/" + (isAngelInvestor ? investorData?.data?.profile_logo?.custom_upload_url : investorData?.data?.profile_logo?.website_logo_url),
        website: typeof investorData?.data?.organization_website === "string" ? String(investorData?.data?.organization_website)?.toLowerCase()?.replaceAll("https://", "") : null,
        location: investorData?.data?.city?.name && investorData?.data?.country?.name ? investorData?.data?.city?.name + ", " + investorData?.data?.country?.name : "-"
    }

    // Guard against "undefined undefined" when the API returns no name.
    const displayName = [investorData?.data?.first_name, investorData?.data?.last_name].filter(Boolean).join(" ") || "-";
    const displayHeading = (typeof profileData?.name === "string" && profileData?.name?.trim()) ? profileData.name : "-";

    // Nothing to claim but the email — no name, website or location saved yet.
    const hasProfileDetails =
        hasText(investorData?.data?.first_name) ||
        hasText(investorData?.data?.last_name) ||
        hasText(investorData?.data?.organization_name) ||
        hasText(investorData?.data?.organization_website) ||
        hasText(investorData?.data?.city?.name) ||
        hasText(investorData?.data?.country?.name);
    const skipClaim = Boolean(investorData?.data?._id) && !hasProfileDetails;

    const [openModal, setOpenModal] = React.useState(false);

    const handleOpen = () => {
        setOpenModal(true);
    }
    const handleClose = () => {
        setOpenModal(false);
    }

    React.useEffect(() => {
        if (!skipClaim) return;
        Cookies.set("X-INV-ID", investor_id);
        navigate("/onboarding", { replace: true });
    }, [skipClaim, investor_id, navigate]);

    if (isLoadingInvestorTypes || isLoadingInvestorData || skipClaim) {
        return <Modal open={isLoadingInvestorTypes || isLoadingInvestorData || skipClaim}>
            <Wrapper className="skeleton" style={{
                display: 'grid', height: '100vh',
                width: "100vw",
                placeItems: 'center'
            }}>
                {/* <CircularProgress sx={{ color: theme?.palette.customColors.b300 }} /> */}
            </Wrapper>
        </Modal>
    }

    return (
        <Box sx={{ minHeight: '100dvh', background: PAGE_BG, display: 'flex', flexDirection: 'column' }}>
            {/* Top bar — logo left, secure badge + logout right (matches onboarding-v2) */}
            <Box
                sx={{
                    background: PAGE_BG,
                    px: { xs: 2.5, sm: 5 },
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                }}
            >
                <img
                    src={BackrrLogo}
                    alt="Backrr"
                    onClick={() => navigateToMain(null)}
                    style={{ height: '34px', width: 'auto', objectFit: 'contain', cursor: 'pointer' }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.8, color: INDIGO }}>
                        <ShieldCheck size={18} strokeWidth={2} />
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: INDIGO }}>
                            Secure &amp; Private
                        </Typography>
                    </Box>
                    {checkForAuthorization() && (
                        <Tooltip title="Logout">
                            <Box
                                role="button"
                                onClick={handleLogOut}
                                sx={{ display: 'flex', cursor: 'pointer', color: MUTED, p: 1 }}
                            >
                                <LogOut size={20} strokeWidth={1.5} />
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Centered content */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
                <Box sx={{ width: '100%', maxWidth: 560 }}>
                    <Typography sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, fontWeight: 700, color: HEADING, lineHeight: 1.2, textAlign: { xs: 'center', sm: 'start' } }}>
                        Investor Profile Found
                    </Typography>
                    <Typography sx={{ color: MUTED, mt: 1, fontSize: '1rem', textAlign: { xs: 'center', sm: 'start' } }}>
                        We found an existing investor profile that matches your domain. You can either activate this profile or create a new one.
                    </Typography>

                    {/* Profile Card */}
                    <SectionCard sx={{ mt: 4, mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <ImageAvatar style={{ borderRadius: "8px" }} width={52} height={52} src={profileData?.image} alt={displayHeading} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: HEADING }}>
                                    {displayHeading}
                                </Typography>
                                <Typography sx={{ fontSize: '0.875rem', color: MUTED }}>
                                    {profileData?.investor_type || "-"}
                                </Typography>
                            </Box>
                        </Box>

                        <Grid container columnSpacing={4} rowSpacing={2.5}>
                            <Grid item xs={6}>
                                <Typography sx={{ fontSize: '0.8rem', color: LABEL, fontWeight: 600, mb: 0.5 }}>
                                    Name
                                </Typography>
                                <Typography sx={{ fontSize: '0.9rem', color: HEADING, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {displayName}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography sx={{ fontSize: '0.8rem', color: LABEL, fontWeight: 600, mb: 0.5 }}>
                                    Email
                                </Typography>
                                <Typography sx={{ fontSize: '0.9rem', color: HEADING, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {profileData?.email || "-"}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography sx={{ fontSize: '0.8rem', color: LABEL, fontWeight: 600, mb: 0.5 }}>
                                    Website
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.9rem',
                                        color: HEADING,
                                        textDecoration: profileData?.website ? 'underline' : 'none',
                                        width: 'min-content',
                                        maxWidth: '100%',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        cursor: profileData?.website ? 'pointer' : 'default',
                                        ":hover": {
                                            color: profileData?.website ? theme => theme.palette.customColors.indigo700 : "",
                                        }
                                    }}
                                    onClick={() => {
                                        if (profileData?.website) window.open("https://" + profileData?.website, "_blank");
                                    }}
                                >
                                    {profileData?.website ?? "-"}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography sx={{ fontSize: '0.8rem', color: LABEL, fontWeight: 600, mb: 0.5 }}>
                                    Location
                                </Typography>
                                <Typography sx={{ fontSize: '0.9rem', color: HEADING, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {profileData?.location}
                                </Typography>
                            </Grid>
                        </Grid>
                    </SectionCard>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Secondary
                            sx={{ flex: 1, py: 1.6, fontWeight: 600 }}
                            onClick={() => {
                                navigate("/onboarding");
                                Cookies.set("X-INV-ID", investor_id);
                            }}
                        >
                            Create New Profile
                        </Secondary>

                        <Primary
                            sx={{ flex: 1, py: 1.6, fontWeight: 600 }}
                            onClick={() => {
                                handleOpen()
                            }}
                        >
                            Activate This Profile
                        </Primary>
                    </Box>
                </Box>
            </Box>

            <Dialog open={openModal} onClose={() => {
                handleClose()
            }} sx={{
                "& > .MuiDialog-container > .MuiPaper-root": {
                    maxWidth: "500px",
                    borderRadius: "8px",
                }
            }}>
                <DialogTitle>
                    <IconButton
                        sx={{ position: "absolute", top: 10, right: 15, padding: 0 }}
                    >
                        <CloseIcon width={40} height={40} onClick={handleClose} />
                    </IconButton>
                    <Typography
                        sx={{
                            fontSize: "1.5rem",
                            fontWeight: 700,
                            lineHeight: "28px",
                            color: theme.palette.customColors.g300,
                            paddingLeft: "0.5rem",
                            textAlign: 'center',
                            marginTop: "0.5rem"
                        }}
                    >
                        Activate Profile
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <PersonalDetails investor_type={investorData?.data?.investor_type} handleClose={handleClose} />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ClaimProfilePage;
